import {TextSearchFilterComponent} from './text-search-filter/text-search-filter.component';
import {JobDepartment} from '../../api/models/job-department';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CacheDataService} from '../../api/services/cache-data.service';
import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, forwardRef, Input, NgZone, OnInit, ViewChild} from '@angular/core';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {JobSeniority} from '../../api/models/job-seniority';
import {Company} from '../../api/models/company';

@Component({
  selector: 'company-search',
  templateUrl: './text-search-filter/text-search-filter.component.html',
  styleUrls: ['./text-search-filter/text-search-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanySearchComponent),
      multi: true
    }
  ]
})
export class CompanySearchComponent extends TextSearchFilterComponent<Company> implements ControlValueAccessor, OnInit, AfterViewInit
{
  @ViewChild('scrollDiv') scrollDiv : ElementRef;
  @Input() fixedHeight?: number;
  @Input() showSelected: boolean = false;

  constructor(cacheDataService: CacheDataService, ngZone: NgZone) {
    super(cacheDataService, ngZone);
  }

  dataUpdated(): void {}

  protected getShowSelected(): boolean {
    return this.showSelected;
  }

  protected getHeightValue() : number | undefined {
    if (this.fixedHeight) {
      return this.fixedHeight;
    }
    return undefined;
  }

  ngOnInit() {
    this.callNgOnInit();
  }

  ngAfterViewInit() {
    this.callNgAfterViewInit();
  }

  protected getDivScroller(): ElementRef {
    return this.scrollDiv;
  }

  protected getIdentifier(target: Company): string {
    return target.key;
  }

  protected isSelected(target: Company) : boolean {
    return this.selectedKeys.has(this.getIdentifier(target));
  }

  protected getDisplay(target: Company): string {
    if (!target.name || target.name.length === 0) {
      return '<NONAME>';
    } else {
      return target.name;
    }
  }

  protected newSearch(hasSearchChanges: boolean, hasSelectedChanges: boolean) {
    this.reloading = true;
    const currentSearch = ++this.searchCounter;

    if (hasSelectedChanges || this.selectedValuesPage === -1) {
      this.selectedValuesPage = 1;
      this.selectedValuesTotalPages = Math.ceil((this._value?.selectedValues?.length ?? 0) / 100);
      this.paginatedSelectedValues = this._value?.selectedValues?.slice(0, 100) ?? [];
    }

    if (this.selectedValuesPage >= this.selectedValuesTotalPages || this.selectedValuesTotalPages <= 1) {
      this.cacheDataService.cacheDataSearchCompanies({
        pageNumber: 1,
        pageSize: 100,
        names: this._value?.searchTerms ?? [],
      }).subscribe(result => {
        if (currentSearch !== this.searchCounter) return;
        this.currentPage = 1;
        this.totalPages = result.totalPages;
        this.sourceValues = result.page;
        this.reloading = false;
      });
    } else {
      this.currentPage = 0;
      this.totalPages = 0;
      this.sourceValues = [];
      this.reloading = false;
    }
  }

  protected getNextBatch() {
    if (this.currentPage < this.totalPages) {
      this.loading = true;
      const currentSearch = this.searchCounter;
      this.currentPage++;
      this.cacheDataService.cacheDataSearchCompanies({
        pageNumber: this.currentPage,
        pageSize: 100,
        names: this._value?.searchTerms ?? [],
      }).subscribe(result => {
        if (currentSearch !== this.searchCounter) return;
        this.totalPages = result.totalPages;
        this.loading = false;
        this.sourceValues = [...this.sourceValues, ...result.page];
      });
    }
  }

  protected getNextSelectedBatch() {
    if (this.selectedValuesPage < this.selectedValuesTotalPages) {
      this.loadingSelected = true;
      this.selectedValuesPage++;
      const nextBatch = this._value.selectedValues.slice((this.selectedValuesPage - 1) * 100, this.selectedValuesPage * 100);
      this.paginatedSelectedValues = [...this.paginatedSelectedValues, ...nextBatch];
      this.loadingSelected = false;

      if (this.selectedValuesPage === this.selectedValuesTotalPages) {
        this.loading = true;
        const currentSearch = this.searchCounter;
        this.cacheDataService.cacheDataSearchCompanies({
          pageNumber: 1,
          pageSize: 100,
          names: this._value?.searchTerms ?? [],
        }).subscribe(result => {
          if (currentSearch !== this.searchCounter) return;
          this.currentPage = 1;
          this.totalPages = result.totalPages;
          this.sourceValues = result.page;
          this.loading = false;
        });
      }
    }
  }

  protected getSearchPlaceholder(): string {
    return 'Company Keyword...';
  }
}
